import * as React from 'react';
import { getIntrinsicElementProps, slot } from '@fluentui/react-components';
import type { SensitivityIconProps, SensitivityIconState } from './SensitivityIcon.types';
import { Shield16Regular, Shield16Filled, ShieldLock16Regular, ShieldLock16Filled } from '@fluentui/react-icons';

/**
 * Create the state required to render SensitivityIcon.
 *
 * The returned state can be modified with hooks such as useSensitivityIconStyles_unstable,
 * before being passed to renderSensitivityIcon_unstable.
 *
 * @param props - props from this instance of SensitivityIcon
 * @param ref - reference to root HTMLElement of SensitivityIcon
 */
export const useSensitivityIcon_unstable = (
  props: SensitivityIconProps,
  ref: React.Ref<HTMLElement>,
): SensitivityIconState => {
  const { fillColor, withLock = false } = props;

  const state: SensitivityIconState = {
    components: {
      root: 'span',
      foregroundIcon: 'span',
      backgroundIcon: 'span',
    },
    root: slot.always(
      getIntrinsicElementProps('span', {
        ref,
        ...props,
      }),
      { elementType: 'span' },
    ),
    foregroundIcon: slot.optional(props.foregroundIcon, {
      renderByDefault: true,
      elementType: 'span',
    }),
    backgroundIcon: slot.optional(props.backgroundIcon, {
      renderByDefault: true,
      elementType: 'span',
    }),
  };

  const iconProvided =
    !!React.Children.count(state.foregroundIcon?.children) || !!React.Children.count(state.backgroundIcon?.children);

  if (!iconProvided) {
    if (state.foregroundIcon) {
      state.foregroundIcon.children = withLock ? <ShieldLock16Regular /> : <Shield16Regular />;
    }
    if (state.backgroundIcon) {
      state.backgroundIcon.children = withLock ? (
        <ShieldLock16Filled primaryFill={fillColor} />
      ) : (
        <Shield16Filled primaryFill={fillColor} />
      );
    }
  }

  return state;
};
